// ---------------------------------------------------------------------------
// Task page — a large centered workspace (Notion/Linear style), not a skinny
// side rail. Wide main column: title, description, sub-tasks, comments.
// Fact grid under the title (Odoo-style): every property in the document.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Archive, Ban, Check, Copy, Eye, FolderInput, Link, MoreHorizontal, Paperclip, Pencil, Plus, RotateCcw, Star, X } from 'lucide-react';
import type { ApprovalState, Assignee, ContentTag, StandardTag, TaskActivity, TaskAttachment, TaskDetail } from '@cg-projects/shared';
import { Icon } from '@/core/ui/icons';
import { useTasksStore } from '@/features/tasks/store/tasksStore';
import { useTaskDetailStore } from '../store/taskDetailStore';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useLookupStore } from '@/features/lookups/store/lookupStore';
import { patchTask, moveStage, createTask, completeWithUndo, closeWithUndo } from '@/features/tasks/mutations';
import { dismissTask } from '@/core/router/router';
import { nav } from '@/core/router/navIntent';
import { taskLink } from '@/core/router/route';
import { onTaskMessage, onTaskSync, joinArea, leaveArea, joinTask, leaveTask } from '@/core/realtime/socket';
import { RichEditor, type RichEditorHandle } from '@/core/ui/RichEditor';
import { toggleCheckboxAt } from '@/core/ui/checklist';
import { api } from '@/core/api/apiClient';
import { useToastStore } from '@/core/ui/toastStore';
import { AssigneeStack, Spinner, Skeleton } from '@/core/ui/atoms';
import { TaskComments } from './TaskComments';
import { CutsSection } from './CutsSection';
import { ContentTagsModal } from './ContentTagsModal';
import { SubtaskList } from './SubtaskList';
import { LinkPreviewCards } from '@/features/preview/LinkPreviewCards';
import { initials, avatarColor } from '@/core/ui/helpers';

const STATES: { value: ApprovalState; label: string }[] = [
  { value: '01_in_progress', label: 'In Progress' },
  { value: '02_changes_requested', label: 'Changes Requested' },
  { value: '04_waiting_normal', label: 'Stuck' },
  { value: '03_approved', label: 'Approved' },
  { value: '1_done', label: 'Done' },
  { value: '1_canceled', label: 'Cancelled' },
];
const CLOSED = new Set(['1_done', '1_canceled']);
const STUCK: ApprovalState = '04_waiting_normal';
// States Approve may fire from — never an already-approved or closed task.

/** "Jul 25, 12:09 PM" from an Odoo UTC datetime string — mirrors TaskComments'
 *  fmtDateTime so the completed banner reads like every other timestamp. */
function fmtClosedAt(iso: string): string {
  const d = new Date(iso.includes('T') ? iso : `${iso.replace(' ', 'T')}Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

// The six CG stores, in the display order migration 005 seeds. DUPLICATED from
// the server's util/storeLocation.ts on purpose — `shared` is type-only (a
// runtime export there breaks the Node strip-types build), so runtime constants
// live twice by house rule.
//
// `code` is what goes on the wire and `name` is what a person reads, and they are
// genuinely different things here: Odoo spells two of these stores 'Deland' and
// 'Winston Salem', every other CG system spells them 'DeLand' and
// 'Winston-Salem'. Sending the code means neither spelling has to win, and the
// day somebody fixes Odoo's, nothing stored has to change.
//
// The server validates every write against the `locations` table, so a drift here
// surfaces as a rejected save with a readable message — never as a bad value
// filed against a store that doesn't exist.
const STORE_LOCATIONS: { code: string; name: string }[] = [
  { code: 'orlando', name: 'Orlando' },
  { code: 'apopka', name: 'Apopka' },
  { code: 'deland', name: 'DeLand' },
  { code: 'sanford', name: 'Sanford' },
  { code: 'durham', name: 'Durham' },
  { code: 'winston-salem', name: 'Winston-Salem' },
];

function escHtml(t: string): string {
  return t.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');
}

/** Description editor — the shared RichEditor (bold/italic/underline/lists/link/
 *  checklist). A failed save keeps the editor + text open; text is cached to
 *  localStorage per task so an interruption/reload can't lose a long brief. */
function DescriptionEditor({ taskId, initialHtml, onSave, onCancel, onUnsavedChange }: {
  taskId: number;
  initialHtml: string;
  /** Resolves to the html the SERVER stored (it may transform it), or null on failure. */
  onSave: (html: string, baseOverride: string | undefined, auto: boolean) => Promise<string | null>;
  onCancel: () => void;
  onUnsavedChange: (unsaved: boolean) => void;
}) {
  const ref = useRef<RichEditorHandle | null>(null);
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<'clean' | 'dirty' | 'saving' | 'saved' | 'failed'>('clean');
  const [autosaved, setAutosaved] = useState(false);
  const key = `cgp:draft:desc:${taskId}`;
  // A draft carries the text it was written AGAINST. Restoring a day-old draft
  // and saving it with today's freshly-fetched baseline would sail past the
  // conflict guard and silently overwrite whatever a teammate wrote meanwhile.
  // The baseline MUST be frozen at mount: `initialHtml` is a live prop that a
  // teammate's task:sync rewrites mid-edit, and re-caching it would persist a
  // baseline the author never saw — re-arming the very bug this guards.
  const cache = () => {
    try {
      const h = ref.current?.getHtml() ?? '';
      if (ref.current?.isEmpty()) localStorage.removeItem(key);
      else localStorage.setItem(key, JSON.stringify({ h, b: frozenBase.current }));
    } catch { /* best-effort */ }
  };
  // ---- Autosave (Emma: "the description is the only thing you have to Save") ----
  // Idle-debounced with a max-wait so a continuous typist still gets checkpoints.
  const AUTOSAVE_IDLE_MS = 2500;
  const AUTOSAVE_MAX_MS = 15000;
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const maxTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(false);
  // What the SERVER currently holds. Autosave skips when the text is byte-identical,
  // and this is the value every baseline is re-armed to after a save.
  const lastSaved = useRef<string>(initialHtml);
  // A failed autosave disarms autosave. Silently retrying a CONFLICT on a timer
  // would eventually overwrite a teammate with no one deciding to — the explicit
  // Save button stays as the deliberate "yes, mine wins" action.
  const autoOff = useRef(false);
  const clearTimers = () => {
    if (idleTimer.current) { clearTimeout(idleTimer.current); idleTimer.current = null; }
    if (maxTimer.current) { clearTimeout(maxTimer.current); maxTimer.current = null; }
  };
  const currentHtml = () => (ref.current?.isEmpty() ? '' : (ref.current?.getHtml() ?? ''));

  // The in-flight save, so an explicit Save pressed mid-autosave can WAIT for it
  // rather than being a silent dead click.
  const flight = useRef<Promise<boolean> | null>(null);

  const runSave = async (auto: boolean): Promise<boolean> => {
    // A cleared editor returns stray <br>/<div> — persist '' so the "No
    // description" affordance survives.
    const html = currentHtml();
    if (auto && html === lastSaved.current) { clearTimers(); setStatus('saved'); onUnsavedChange(false); return true; }
    inFlight.current = true;
    clearTimers();
    if (!auto) setBusy(true);
    setStatus('saving');
    const stored = await onSave(html, draftBase.current, auto);
    inFlight.current = false;
    if (!auto) setBusy(false);
    if (stored !== null) {
      // Re-arm EVERY baseline to what the server now holds. Omitting this is why
      // description saving has 409'd against itself twice before: the second save
      // would still be compared against the text the session started from.
      lastSaved.current = stored;
      draftBase.current = stored;
      frozenBase.current = stored;
      // A save that lands means the conflict is resolved, so autosave comes back.
      // Leaving it disarmed for the rest of the session left the user typing under
      // a status line that no longer matched reality.
      autoOff.current = false;
      if (auto) setAutosaved(true);
      // Typing continues DURING the round trip. Treating the save as "all caught
      // up" would delete the draft and disarm the close guard while newer text is
      // still only in the editor — one Done/reload away from losing it.
      if (currentHtml() !== stored) {
        cache();
        setStatus(autoOff.current ? 'failed' : 'dirty');
        onUnsavedChange(true);
        schedule();
      } else {
        setStatus('saved');
        onUnsavedChange(false);
        try { localStorage.removeItem(key); } catch { /* ignore */ }
      }
      return true;
    }
    setStatus('failed');
    if (auto) autoOff.current = true;
    // Conflict shown once: drop the draft's stale baseline so pressing Save again
    // is a deliberate overwrite against the refreshed text, not a 409 dead end.
    else draftBase.current = undefined;
    return false;
  };

  const doSave = async (auto: boolean): Promise<boolean> => {
    if (inFlight.current) {
      // An autosave already covers this text; a queued duplicate would just race.
      if (auto) return false;
      // But an explicit Save must never do nothing: wait, then save what's there now.
      await flight.current?.catch(() => false);
    }
    const p = runSave(auto);
    flight.current = p;
    return p;
  };

  const schedule = () => {
    if (autoOff.current) return;
    if (idleTimer.current) clearTimeout(idleTimer.current);
    // Each callback clears its OWN ref: a timer that fires while a save is in
    // flight returns early, and a stale non-null ref would then block the
    // `!maxTimer.current` re-arm below for the rest of the session.
    idleTimer.current = setTimeout(() => { idleTimer.current = null; void doSave(true); }, AUTOSAVE_IDLE_MS);
    if (!maxTimer.current) maxTimer.current = setTimeout(() => { maxTimer.current = null; void doSave(true); }, AUTOSAVE_MAX_MS);
  };
  const onInput = () => {
    cache();
    const dirty = currentHtml() !== lastSaved.current;
    // Once autosave is disarmed, "Saves automatically…" would be a lie.
    setStatus(dirty ? (autoOff.current ? 'failed' : 'dirty') : 'saved');
    onUnsavedChange(dirty);
    schedule();
  };

  // A reload/close mid-sentence: flush the draft to localStorage synchronously
  // (an authenticated PATCH can't be relied on during unload) and warn.
  useEffect(() => {
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      if (currentHtml() === lastSaved.current) return;
      cache();
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => { window.removeEventListener('beforeunload', onBeforeUnload); clearTimers(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = () => { void doSave(false); };
  /** "Done" = keep it. Anything typed since the last autosave (up to 2.5s of
   *  writing) is still only in the editor, so this MUST flush before closing —
   *  clearing the draft and closing would silently bin it. */
  const done = async () => {
    if (currentHtml() !== lastSaved.current) { if (!(await doSave(false))) return; }
    clearTimers();
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    onUnsavedChange(false);
    onCancel();
  };
  /** "Cancel" = throw it away. Only offered before anything has been saved, and
   *  it asks first when there is actually something to lose. */
  const cancel = () => {
    if (currentHtml() !== lastSaved.current
      && !window.confirm('Discard what you typed here?')) return;
    clearTimers();
    try { localStorage.removeItem(key); } catch { /* ignore */ }
    onUnsavedChange(false);
    onCancel();
  };
  const toast = useToastStore((s) => s.show);
  // A pasted screenshot goes to the task's Attachments (not inlined as base64,
  // which would bloat the Odoo description field) — consistent with comments.
  const onPasteFiles = (files: File[]): boolean => {
    const images = files.filter((f) => f.type.startsWith('image/'));
    if (!images.length) return false;
    void (async () => {
      for (const f of images) {
        if (f.size > 15_000_000) { toast(`${f.name} is over 15 MB — skipped.`, 'error'); continue; }
        try {
          const dataBase64 = await new Promise<string>((res, rej) => { const rd = new FileReader(); rd.onload = () => res(String(rd.result).split(',')[1] ?? ''); rd.onerror = () => rej(new Error('read')); rd.readAsDataURL(f); });
          await api.post(`/tasks/${taskId}/attachments`, { name: f.name && f.name !== 'image.png' ? f.name : `pasted-${Date.now()}.png`, dataBase64, mimetype: f.type || undefined }, { timeout: 120000 });
        } catch (e) { toast(e instanceof Error ? e.message : 'Upload failed.', 'error'); }
      }
      toast('Pasted image added to Attachments.', 'info');
    })();
    return true;
  };
  // Restore a saved draft (from a prior interrupted edit) over the stored html,
  // and carry ITS baseline so a stale draft 409s instead of clobbering.
  let seed = initialHtml;
  let restoredBase: string | undefined;
  try {
    const d = localStorage.getItem(key);
    if (d !== null) {
      if (d.startsWith('{')) {
        const parsed = JSON.parse(d) as { h?: unknown; b?: unknown };
        if (typeof parsed.h === 'string') { seed = parsed.h; if (typeof parsed.b === 'string') restoredBase = parsed.b; }
      } else {
        seed = d; // pre-1.9.2 plain-string draft — no baseline recorded
      }
    }
  } catch { /* none */ }
  const draftBase = useRef<string | undefined>(restoredBase);
  // The text this editing session started from, captured ONCE. A restored draft
  // keeps its own original baseline; a fresh edit freezes today's.
  const frozenBase = useRef<string>(restoredBase ?? initialHtml);
  return (
    <div className="mb-6">
      <RichEditor ref={ref} initialHtml={seed} autoFocus minHeight="140px" placeholder="Add details… bold, links, and checklists all work here." onInput={onInput} onSubmitShortcut={save} onPasteFiles={onPasteFiles} />
      <div className="mt-1.5 flex items-center gap-2">
        <button onClick={save} disabled={busy}
          className="rounded bg-brand px-2.5 py-0.5 text-xs font-semibold text-page hover:brightness-110 disabled:opacity-50">{busy ? 'Saving…' : 'Save'}</button>
        {/* Once something has autosaved there is nothing left to discard, so
            offering "Cancel" would be a lie about what the button does. */}
        <button onClick={autosaved ? () => void done() : cancel} className="text-xs text-muted hover:text-ink">{autosaved ? 'Done' : 'Cancel'}</button>
        <span className={`text-[10px] ${status === 'failed' ? 'text-danger' : 'text-faint'}`}>
          {status === 'saving' ? 'Saving…'
            : status === 'saved' ? 'Saved'
              : status === 'failed' ? "Couldn't save — press Save to retry"
                : status === 'dirty' ? 'Saves automatically…' : ''}
        </span>
        <span className="ml-auto hidden text-[10px] text-faint sm:inline">⌘/Ctrl+Enter to save</span>
      </div>
    </div>
  );
}

function fmtSize(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

/** Attachments — thumbnails for images, chips for files; upload + drag-drop. */
function AttachmentsSection({ taskId }: { taskId: number }) {
  const [files, setFiles] = useState<TaskAttachment[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const toast = useToastStore((s) => s.show);

  useEffect(() => {
    let alive = true;
    setFiles(null);
    api.get<{ attachments: TaskAttachment[] }>(`/tasks/${taskId}/attachments`)
      .then((r) => { if (alive) setFiles(r.attachments); })
      .catch(() => { if (alive) setFiles([]); });
    return () => { alive = false; };
  }, [taskId]);

  const upload = async (list: FileList | File[]) => {
    if (busy) return;
    setBusy(true);
    try {
      for (const f of Array.from(list)) {
        if (f.size > 15_000_000) { toast(`${f.name} is over 15 MB — skipped.`, 'error'); continue; }
        const dataBase64 = await new Promise<string>((resolve, reject) => {
          const rd = new FileReader();
          rd.onload = () => resolve(String(rd.result).split(',')[1] ?? '');
          rd.onerror = () => reject(new Error('read failed'));
          rd.readAsDataURL(f);
        });
        const { attachment } = await api.post<{ attachment: TaskAttachment }>(`/tasks/${taskId}/attachments`, {
          name: f.name, dataBase64, mimetype: f.type || undefined,
        }, { timeout: 120000 });
        setFiles((prev) => [attachment, ...(prev ?? [])]);
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Upload failed.', 'error');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async (att: TaskAttachment) => {
    if (!window.confirm(`Delete "${att.name}"? There is no undo.`)) return;
    try {
      await api.delete(`/tasks/${taskId}/attachments/${att.id}`);
      setFiles((prev) => (prev ?? []).filter((f) => f.id !== att.id));
    } catch (e) {
      toast(e instanceof Error ? e.message : 'Could not delete.', 'error');
    }
  };

  // A teammate's upload announces itself in chatter (📎) — refresh the list live.
  useEffect(() => {
    return onTaskMessage((e) => {
      // Refresh on a standalone 📎 upload OR any comment that carried files
      // (a text+attachment comment has no 📎 marker but does have attachments).
      if (e.taskId !== taskId || (!e.message.body.includes('📎') && !(e.message.attachments?.length))) return;
      api.get<{ attachments: TaskAttachment[] }>(`/tasks/${taskId}/attachments`)
        .then((r) => setFiles(r.attachments))
        .catch(() => { /* keep current list */ });
    });
  }, [taskId]);

  // Ctrl+V a screenshot anywhere on the task page → it attaches. Designers
  // paste constantly; text pastes into inputs are untouched (no image files),
  // and pastes INTO the rich description editor keep their native behavior.
  useEffect(() => {
    const onPaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented) return; // the comment box claimed this paste
      const target = e.target as HTMLElement | null;
      if (target?.isContentEditable) return; // description editor owns its pastes
      const images = Array.from(e.clipboardData?.files ?? []).filter((f) => f.type.startsWith('image/'));
      if (!images.length) return;
      e.preventDefault();
      if (busy) { toast('Still uploading the previous file — paste again in a moment.', 'error'); return; }
      const stamped = images.map((f, i) => new File([f], f.name && f.name !== 'image.png' ? f.name : `pasted-${Date.now()}${i ? `-${i}` : ''}.png`, { type: f.type }));
      void upload(stamped);
      toast(`Attaching ${stamped.length === 1 ? 'pasted image' : `${stamped.length} pasted images`}…`, 'info');
    };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, busy]);

  const images = (files ?? []).filter((f) => f.isImage);
  const docs = (files ?? []).filter((f) => !f.isImage);

  return (
    <div
      // An EMPTY section is an affordance, not a container: no dashed box
      // drawing the eye to nothing ("this page still feels off" — Allyson,
      // 8/10). The box materializes the moment files exist or a drag enters.
      className={`mb-6 rounded-lg border p-3 transition ${dragOver ? 'border-dashed border-brand bg-brand/10' : (files?.length ?? 0) > 0 ? 'border-dashed border-line' : 'border-transparent px-0'}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files.length) void upload(e.dataTransfer.files); }}
    >
      <div className="mb-2 flex items-center gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-faint">
          Attachments{files?.length ? ` · ${files.length}` : ''}
        </span>
        <button
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          className="ml-auto flex items-center gap-1 rounded-md border border-line px-2 py-0.5 text-[11px] text-muted transition hover:bg-hover hover:text-ink disabled:opacity-40"
        >
          {busy ? 'Uploading…' : <><Icon i={Plus} size={14} /> Attach files</>}
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => e.target.files && void upload(e.target.files)} />
      </div>

      {files === null && <div className="text-xs text-faint">Loading…</div>}
      {files !== null && files.length === 0 && (
        <div className="py-0.5 text-[11px] text-faint">Drop files anywhere here, or use Attach files.</div>
      )}

      {images.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {images.map((f) => (
            <div key={f.id} className="group/att relative">
              <a href={f.url} target="_blank" rel="noopener">
                <img src={f.url} alt={f.name} title={`${f.name} · ${fmtSize(f.fileSize)}`} className="h-24 w-24 rounded-md border border-line object-cover" loading="lazy" />
              </a>
              <button onClick={() => void remove(f)} title="Delete"
                className="absolute -right-1.5 -top-1.5 hidden h-5 w-5 items-center justify-center rounded-full border border-line bg-canvas text-muted hover:text-danger group-hover/att:flex"><Icon i={X} size={14} /></button>
            </div>
          ))}
        </div>
      )}
      {docs.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {docs.map((f) => (
            <span key={f.id} className="group/att inline-flex items-center gap-1.5 rounded-md border border-line bg-canvas px-2 py-1 text-[12px]">
              <Icon i={Paperclip} size={14} /> <a href={f.url} target="_blank" rel="noopener" className="max-w-[220px] truncate text-ink hover:underline">{f.name}</a>
              <span className="text-[10px] text-faint">{fmtSize(f.fileSize)}</span>
              <button onClick={() => void remove(f)} className="hidden text-faint hover:text-danger group-hover/att:inline" title="Delete"><Icon i={X} size={14} /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/** One fact row in the document grid — see the comment inside. */
function Prop({ label, children }: { label: string; children: ReactNode }) {
  // Odoo's form row: label in a fixed left gutter, value beside it. These used
  // to stack vertically for a 288px rail; in the document grid the horizontal
  // shape is what makes twelve fields read as a compact fact block, not a form.
  return (
    <div className="grid grid-cols-[108px_minmax(0,1fr)] items-start gap-x-3 py-1">
      <div className="pt-[7px] text-[10px] font-semibold uppercase tracking-wide text-faint">{label}</div>
      <div className="min-w-0 text-[13px] text-ink">{children}</div>
    </div>
  );
}

/** "2.4M" / "40.2K" / "812" — the scoreboard's display voice. */
function fmtViews(n: number | null | undefined): string {
  if (n == null) return '';
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(n % 1_000 === 0 ? 0 : 1)}K`;
  return String(n);
}
/** Accepts what a human types off the Instagram screen: "2.4m", "40k",
 *  "812", "1,234,567". null = empty (clear); NaN = garbage. */
function parseViews(raw: string): number | null {
  const t = raw.trim().toLowerCase().replace(/[\s,]/g, '');
  if (!t) return null;
  const m = /^([0-9]*\.?[0-9]+)([km])?$/.exec(t);
  if (!m) return Number.NaN;
  const n = parseFloat(m[1]) * (m[2] === 'm' ? 1_000_000 : m[2] === 'k' ? 1_000 : 1);
  return Math.round(n);
}

const ctrl = 'rounded-md border border-line bg-page px-2 py-1 text-[13px] text-ink outline-none focus:border-brand';
// Fact-grid controls read as TEXT until touched — Odoo's form trick, and the
// answer to "still hard to read": ten full-width bordered boxes made the fact
// block shout like a settings form. Chrome appears on hover/focus; width hugs
// the value instead of stretching across the column.
const fieldCtrl = 'w-auto max-w-full rounded-md border border-transparent bg-transparent px-2 py-1 text-[13px] text-ink outline-none transition hover:border-line hover:bg-page focus:border-brand focus:bg-page';

/** True below Tailwind's md (768px) — the phone/small-tablet reality. */
function useIsPhone(): boolean {
  const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

export function TaskDetailPanel() {
  const task = useTaskDetailStore((s) => s.task);
  const loading = useTaskDetailStore((s) => s.loading);
  const error = useTaskDetailStore((s) => s.error);
  const open = useTaskDetailStore((s) => s.open);
  const stages = useAreaStore((s) => (task ? s.stagesByArea[task.areaId] : undefined));
  const area = useAreaStore((s) => (task ? s.areas.find((a) => a.id === task.areaId) : undefined));
  const lookup = useLookupStore();
  const loadLookups = useLookupStore((s) => s.load);

  const [nameDraft, setNameDraft] = useState('');
  // v1.91.0 — content-tag add/manage state (hooks stay above every early return).
  const [newTagOpen, setNewTagOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [manageTagsOpen, setManageTagsOpen] = useState(false);
  const [memberIds, setMemberIds] = useState<number[] | null>(null);
  const [watchers, setWatchers] = useState<{ watchers: Assignee[]; watching: boolean } | null>(null);
  const [editingDesc, setEditingDesc] = useState(false);
  const [subDraft, setSubDraft] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [moveTarget, setMoveTarget] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [stuckOpen, setStuckOpen] = useState(false);
  const [stuckReason, setStuckReason] = useState('');
  // "Request changes asks why" — same capture pattern as Stuck, one motion:
  // the reason PATCHes with the state, the verdict card carries it, the strip
  // shows it until the next state change clears it.
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesReason, setChangesReason] = useState('');
  const [changesBusy, setChangesBusy] = useState(false);
  const [stuckBusy, setStuckBusy] = useState(false);
  const [commentsKey, setCommentsKey] = useState(0);
  // Tracked-change trail for the Activity river — fetched EAGERLY with the task
  // (the retired right-rail collapsible fetched it lazily; the river IS the
  // record now, so it must be there when the timeline paints). null = loading
  // or unavailable — TaskComments degrades to conversation-only, never blocks.
  // commentsKey is a dep so the Stuck flow's remount also re-pulls the trail.
  const [activityRows, setActivityRows] = useState<TaskActivity[] | null>(null);
  useEffect(() => {
    setActivityRows(null);
    if (!task?.id) return;
    let alive = true;
    api.get<{ activity: TaskActivity[] }>(`/tasks/${task.id}/activity`)
      .then((r) => { if (alive) setActivityRows(r.activity); })
      .catch(() => { if (alive) setActivityRows(null); });
    return () => { alive = false; };
  }, [task?.id, commentsKey]);
  // Sub-task navigation (Destiny: "I can't get back to the parent or to the next
  // sub-task like Odoo does"). Populated when the open task is a sub-task.
  const [subNav, setSubNav] = useState<{ parentId: number; parentName: string; siblings: { id: number; name: string }[]; index: number } | null>(null);
  const loadStages = useAreaStore((s) => s.loadStages);
  useEffect(() => { void loadLookups(); }, [loadLookups]);
  useEffect(() => {
    setSubNav(null);
    if (!task?.parentId) return;
    let alive = true;
    api.get<{ task: TaskDetail }>(`/tasks/${task.parentId}`)
      .then((r) => {
        if (!alive) return;
        const sibs = r.task.children.map((c) => ({ id: c.id, name: c.name }));
        setSubNav({ parentId: r.task.id, parentName: r.task.name, siblings: sibs, index: sibs.findIndex((s) => s.id === task.id) });
      })
      .catch(() => { if (alive) setSubNav(null); });
    return () => { alive = false; };
  }, [task?.id, task?.parentId]);
  useEffect(() => { setNameDraft(task?.name ?? ''); }, [task?.id, task?.name]);
  useEffect(() => { setStuckOpen(false); setStuckReason(''); setChangesOpen(false); setChangesReason(''); setChangesBusy(false); setEditingDesc(false); setSubDraft(''); setMenuOpen(false); setMoveTarget(false); }, [task?.id]);
  // Stages may not be loaded when arriving from My Work / search / inbox.
  useEffect(() => { if (task?.areaId) void loadStages(task.areaId); }, [task?.areaId, loadStages]);

  // Esc / backdrop / ✕ close — but never while typing (incl. the contentEditable
  // description), and never silently while unsaved description edits exist.
  const editingDescRef = useRef(false);
  // The DESCRIPTION CONTENT the editor started from — frozen at edit-start so a
  // genuine concurrent description change 409s, while unrelated field edits do
  // NOT (content comparison, not whole-record write_date).
  const descBaseHtml = useRef<string>('');
  // Description writes THIS client made. The task:updated echo of our own autosave
  // must never raise "someone else just edited this" — with a save every couple of
  // seconds that alarm would fire constantly and always be wrong.
  const selfDescWrites = useRef<string[]>([]);
  const selfDescInFlight = useRef(0);
  const markSelfDescWrite = (html: string, inFlight = true) => {
    selfDescWrites.current.push(html);
    if (selfDescWrites.current.length > 20) selfDescWrites.current.splice(0, selfDescWrites.current.length - 20);
    if (inFlight) selfDescInFlight.current += 1;
  };
  const endSelfDescWrite = () => { selfDescInFlight.current = Math.max(0, selfDescInFlight.current - 1); };
  // Unsaved = the editor holds text the server does not. With autosave that is
  // usually false, so closing no longer nags about discarding nothing.
  const [descUnsaved, setDescUnsaved] = useState(false);
  const descUnsavedRef = useRef(false);
  descUnsavedRef.current = descUnsaved;
  const setDirty = useTaskDetailStore((s) => s.setDirty);
  // Stamped ONLY when editing opens. This effect must never depend on anything
  // that changes mid-edit: re-running it re-points the conflict baseline at the
  // CURRENT store value — which may be a teammate's just-synced text — and the
  // first save would then overwrite them instead of 409ing.
  useEffect(() => {
    editingDescRef.current = editingDesc;
    if (editingDesc) {
      // §5A guard: only a FETCHED task may arm a save baseline. A seeded partial
      // (instant open) carries writeDate '' and an empty descriptionHtml the
      // server never sent — a baseline stamped from it would let the first save
      // clobber (or spuriously 409 against) the real description. The skeleton
      // zone makes this unreachable from the UI; this is the belt for that braces.
      const t = useTaskDetailStore.getState().task;
      if (!t?.writeDate) { setEditingDesc(false); return; }
      descBaseHtml.current = t.descriptionHtml;
    } else setDescUnsaved(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingDesc]);
  // Any navigation into another task (deep link, notify toast "Open", sub-task
  // click) confirms before discarding — the store gates open(). Separate effect so
  // the dirty flag can track live edits without disturbing the baseline above.
  useEffect(() => {
    setDirty(editingDesc && descUnsaved);
  }, [editingDesc, descUnsaved, setDirty]);
  const requestClose = () => {
    if (editingDescRef.current && descUnsavedRef.current) {
      if (!window.confirm('You have unsaved description edits — discard them?')) return;
      // Discard means discard — drop the cached draft so it can't resurrect.
      if (task) { try { localStorage.removeItem(`cgp:draft:desc:${task.id}`); } catch { /* ignore */ } }
      // The discard is DECIDED here. dismissTask() below routes through
      // history.back() → closeFromRoute(), which reads store `dirty` to decide
      // whether to promise "Draft kept" — leaving it true would tell the user
      // their text survived, one tick after we deleted it.
      setDescUnsaved(false);
      setDirty(false);
    }
    // dismissTask CONSUMES the history entry that opened this task (history.back())
    // instead of pushing a new one — otherwise Back would re-open the task the
    // user just dismissed. The confirm above runs FIRST, so Cancel is a total
    // no-op and the store can never desync from the URL.
    dismissTask();
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      const t = e.target as HTMLElement | null;
      if (t && (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || t.isContentEditable)) return;
      requestCloseRef.current();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Membership of this project — assigning a non-member warns (access is
  // membership-only; an assignee without membership can't see the task).
  const toastWarn = useToastStore((s) => s.show);

  // Cross-project live sync: the socket only auto-joins the SELECTED board's
  // room, so a task opened from My Work / Inbox / search / a notification /
  // deep link would get NO live comments or updates. Join its area room while
  // the panel is open (refcounted, so the board's own room is untouched).
  useEffect(() => {
    if (!task?.areaId) return;
    joinArea(task.areaId);
    return () => leaveArea(task.areaId);
  }, [task?.areaId]);

  // ALSO join the per-task room: a non-member ASSIGNEE (task-level access) is
  // refused the area room by the server, so this is their only live channel.
  // Members get task events via the area room anyway; the server dedupes.
  useEffect(() => {
    if (!task?.id) return;
    joinTask(task.id);
    return () => leaveTask(task.id);
  }, [task?.id]);

  // A teammate's write refreshes the OPEN panel (incl. the description, which
  // list syncs strip). The store update is safe even mid-edit — the editor
  // seeds its text once and is insulated — and it keeps writeDate FRESH so a
  // later save doesn't 409 over an unrelated field change. Only a genuine
  // remote description change warrants a warning.
  useEffect(() => {
    return onTaskSync((e) => {
      if (!task || e.task.id !== task.id) return;
      const descChanged = e.task.descriptionHtml !== task.descriptionHtml;
      // The broadcast deliberately carries EMPTY blockedBy/blocks (they name
      // tasks in other projects and a room broadcast can't be redacted
      // per-reader) — keep the access-checked refs this panel already fetched
      // instead of blanking the Blocked-by section on every teammate write.
      useTaskDetailStore.setState({ task: { ...e.task, blockedBy: task.blockedBy, blocks: task.blocks } });
      // Ours? Either a write is still in flight (the echo beat the response) or the
      // incoming html is one we sent / the server stored for us.
      const mine = selfDescInFlight.current > 0 || selfDescWrites.current.includes(e.task.descriptionHtml);
      if (editingDescRef.current && descChanged && !mine) {
        toastWarn('Heads up — someone else just edited this description. Saving will overwrite their version.', 'info');
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task?.id, task?.descriptionHtml]);
  useEffect(() => {
    setMemberIds(null);
    if (!task?.areaId) return;
    let alive = true;
    api.get<{ members: Assignee[] }>(`/areas/${task.areaId}/members`)
      .then((r) => { if (alive) setMemberIds(r.members.map((m) => m.id)); })
      .catch(() => { if (alive) setMemberIds(null); });
    return () => { alive = false; };
  }, [task?.areaId]);
  const isMember = (uid: number) => memberIds === null || memberIds.includes(uid);

  // Watchers — passive visibility: followers get every comment in their inbox.
  useEffect(() => {
    setWatchers(null);
    if (!task?.id) return;
    let alive = true;
    api.get<{ watchers: Assignee[]; watching: boolean }>(`/tasks/${task.id}/watchers`)
      .then((r) => { if (alive) setWatchers(r); })
      .catch(() => { if (alive) setWatchers(null); });
    return () => { alive = false; };
  }, [task?.id]);

  const toggleWatch = async () => {
    if (!task || !watchers) return;
    const next = !watchers.watching;
    try {
      const r = await api.post<{ watchers: Assignee[]; watching: boolean }>(`/tasks/${task.id}/watch`, { watch: next });
      setWatchers(r);
      toastWarn(next ? 'Watching — comments on this task will land in your inbox.' : 'No longer watching.', 'info');
    } catch (e) {
      toastWarn(e instanceof Error ? e.message : 'Could not update watching.', 'error');
    }
  };

  const isPhone = useIsPhone();
  const isContent = Boolean(area?.isContentTeam);
  const done = task ? CLOSED.has(task.state) : false;
  const isStuck = task ? task.state === STUCK : false;

  // ---- THE STATUS STRIP: one plain sentence saying what the card needs -----
  // (best-in-class release #1, Allyson-greenlit). Approve appears ONLY when
  // the card is genuinely asking: its stage is one where approving ADVANCES
  // the card (approveStages — config-driven, excludes destinations like
  // 'Published' where approvals merely land; never name-matching in code), or a
  // reviewer sent it back and it awaits re-approval.
  const approvalStage = Boolean(task && area?.approveStages?.some(
    (n) => n.toLowerCase() === (task.stageName ?? '').toLowerCase()));
  const showApprove = Boolean(task && !done
    && ((approvalStage && task.state === '01_in_progress') || task.state === '02_changes_requested'));
  // Days the card has sat in its current stage — from the newest stage change
  // in the trail (already fetched for the activity river); null (age simply
  // omitted) when it has never moved or the trail hasn't loaded yet.
  const stageAgeDays: number | null = (() => {
    if (!task) return null;
    const stamp = (activityRows ?? []).find((a) => a.changes.some((c) => /stage/i.test(c.field)))?.date ?? null;
    if (!stamp) return null;
    const ms = new Date(stamp.includes('T') ? stamp : `${stamp.replace(' ', 'T')}Z`).getTime();
    return Number.isNaN(ms) ? null : Math.max(0, Math.floor((Date.now() - ms) / 86_400_000));
  })();
  // Who sent it back — the newest trail row moving status INTO changes-requested.
  const changesBy: string | null = task?.state === '02_changes_requested'
    ? (activityRows ?? []).find((a) => a.changes.some((c) => c.to === '02_changes_requested'))?.authorName ?? null
    : null;
  const firstNames = (task?.assignees ?? []).slice(0, 2).map((a) => a.name.split(' ')[0]).join(' + ');
  const strip: { text: string; tone: 'danger' | 'warn' | 'muted' } | null = (() => {
    if (!task || done) return null;
    const age = stageAgeDays !== null && stageAgeDays > 0
      ? ` · ${stageAgeDays} ${stageAgeDays === 1 ? 'day' : 'days'} in this step` : '';
    if (isStuck) return { text: `Stuck — ${task.stateReason ?? 'reason in the conversation'}`, tone: 'danger' };
    if (task.state === '02_changes_requested') {
      return { text: `Changes requested${changesBy ? ` by ${changesBy}` : ''} — ${task.stateReason ?? 'see the conversation'}`, tone: 'warn' };
    }
    if (task.state === '03_approved') return { text: `Approved — in ${task.stageName ?? 'its stage'}`, tone: 'muted' };
    if (approvalStage) {
      return { text: `Waiting on approval — in ${task.stageName ?? 'review'}${age}`, tone: stageAgeDays !== null && stageAgeDays > 7 ? 'warn' : 'muted' };
    }
    return {
      text: `In ${task.stageName ?? 'progress'}${firstNames ? ` — ${firstNames} on it` : ' — nobody assigned yet'}${age}`,
      tone: stageAgeDays !== null && stageAgeDays > 7 ? 'warn' : 'muted',
    };
  })();

  const areasAll = useAreaStore((s) => s.areas);
  const removeFromBoard = (taskId: number, areaId: number) => {
    useTasksStore.setState((s) => {
      const tba = { ...s.tasksByArea };
      if (tba[areaId]) tba[areaId] = tba[areaId].filter((x) => x.id !== taskId);
      return { tasksByArea: tba };
    });
  };

  const archiveTask = async () => {
    if (!task) return;
    if (!window.confirm(`Archive "${task.name}"? It disappears from all views (recoverable from Odoo archives).`)) return;
    try {
      await api.post(`/tasks/${task.id}/archive`, {});
      removeFromBoard(task.id, task.areaId);
      toastWarn('Task archived.', 'info');
      // dismissTask, not a bare store close(): archiving is also a CLOSE PATH, and
      // it must retire the panel's history entry the same way ✕/Esc do. A bare
      // close() replaced the entry with the board hash and left a duplicate board
      // entry beneath it for Back to wade through.
      dismissTask();
    } catch (e) {
      toastWarn(e instanceof Error ? e.message : 'Could not archive.', 'error');
    }
  };

  const duplicateTask = async () => {
    if (!task) return;
    try {
      const { task: copy } = await api.post<{ task: TaskDetail }>(`/tasks/${task.id}/duplicate`, { name: `${task.name} (copy)` });
      // The server's task:sync carries our own client id, so THIS tab ignores it —
      // put the copy on our board ourselves or it's missing until a reload.
      useTasksStore.getState().applyRemote({ areaId: copy.areaId, task: copy });
      setMenuOpen(false);
      // open() can REFUSE (unsaved description → the user kept their draft). Don't
      // tell them they're looking at the copy when they're still on the original.
      // REPLACE (in-panel travel): the panel swaps to the copy in place — one
      // history entry. The tx returns open()'s promise; its sync prefix (veto +
      // openId) runs inside the tx and the await happens safely after the flush.
      const viewing = await nav.tx('replace', () => open(copy.id));
      toastWarn(viewing
        ? 'Duplicated — you are now viewing the copy.'
        : 'Duplicated — the copy is on the board; your draft is still open here.', 'info');
    } catch (e) {
      toastWarn(e instanceof Error ? e.message : 'Could not duplicate.', 'error');
    }
  };

  // Blocked-by is a FULL-REPLACE write, and the panel's task.blockedBy can be
  // stale — the live broadcast strips refs (they can't be per-reader redacted),
  // so a teammate adding a blocker never reaches this panel. Rebuilding the id
  // list off that stale copy would erase their addition. So read the
  // authoritative deps immediately before writing, mutate THAT, and send it.
  //
  // SERIALIZED: each edit chains after the previous one's PATCH commits — two
  // rapid clicks (remove A then remove B) otherwise both GET the pre-mutation
  // list and the second PATCH resurrects the first's removal (last-write-wins).
  const depChain = useRef<Promise<unknown>>(Promise.resolve());
  const editBlockedBy = (mutate: (ids: number[]) => number[]) => {
    if (!task) return;
    const taskId = task.id;
    depChain.current = depChain.current
      .catch(() => undefined)
      .then(async () => {
        try {
          const { task: fresh } = await api.get<{ task: TaskDetail }>(`/tasks/${taskId}`);
          await patchTask(taskId, { blockedByIds: mutate(fresh.blockedBy.map((r) => r.id)) });
        } catch (e) {
          toastWarn(e instanceof Error ? e.message : 'Could not update dependencies.', 'error');
        }
      });
  };

  const moveToProject = async (targetAreaId: number) => {
    if (!task || targetAreaId === task.areaId) return;
    try {
      const { stages: targetStages } = await api.get<{ stages: { id: number }[] }>(`/areas/${targetAreaId}/stages`);
      const stageId = targetStages[0]?.id;
      if (!stageId) { toastWarn('That project has no stages.', 'error'); return; }
      const prevArea = task.areaId;
      const { task: moved } = await api.post<{ task: TaskDetail }>(`/tasks/${task.id}/move`, { stageId, areaId: targetAreaId });
      removeFromBoard(task.id, prevArea);
      // Our own task:sync carries this client's id, so this tab ignores it — put
      // the card on the DESTINATION board ourselves (same as duplicateTask).
      useTasksStore.getState().applyRemote({ areaId: moved.areaId, task: moved });
      toastWarn('Task moved.', 'info');
      setMenuOpen(false); setMoveTarget(false);
      // NOT open(task.id): open() early-returns on the task already on screen, so
      // it would leave the header naming the old project and the stage dropdown
      // listing the source project's stages. refresh() has no such guard and keeps
      // the panel mounted (no spinner flash, no lost scroll position).
      void useTaskDetailStore.getState().refresh();
    } catch (e) {
      toastWarn(e instanceof Error ? e.message : 'Could not move the task.', 'error');
    }
  };

  const confirmStuck = async () => {
    const reason = stuckReason.trim();
    if (!task || !reason || stuckBusy) return;
    setStuckBusy(true);
    // stateReason rides the same PATCH — the strip shows it inline (no more
    // "the reason is in the comments" treasure hunt).
    const ok = await patchTask(task.id, { state: STUCK, stateReason: reason }, { state: STUCK });
    if (ok) {
      try { await api.post(`/tasks/${task.id}/messages`, { body: escHtml(`🚫 Stuck: ${reason}`) }); } catch { /* non-fatal */ }
      setStuckReason('');
      setStuckOpen(false);
      setCommentsKey((k) => k + 1);
    }
    setStuckBusy(false);
  };

  const confirmChanges = async () => {
    const reason = changesReason.trim();
    if (!task || !reason || changesBusy) return;
    setChangesBusy(true);
    const ok = await patchTask(task.id,
      { state: '02_changes_requested', stateReason: reason },
      { state: '02_changes_requested' });
    if (ok) {
      setChangesReason('');
      setChangesOpen(false);
      setCommentsKey((k) => k + 1); // the verdict card (with the reason) just landed
    }
    setChangesBusy(false);
  };

  // Same-area open tasks that could block this one (excludes self + already-linked).
  const areaTasks = useTasksStore((s) => (task ? s.tasksByArea[task.areaId] : undefined));

  if (!task && !loading && !error) return null;

  const saveName = () => {
    const v = nameDraft.trim();
    if (task && v && v !== task.name) void patchTask(task.id, { name: v }, { name: v });
  };

  const setAssignees = (next: Assignee[]) =>
    task && void patchTask(task.id, { assigneeIds: next.map((a) => a.id) }, { assignees: next });

  const setContentTags = (next: ContentTag[]) =>
    task && void patchTask(task.id, { contentTagIds: next.map((t) => t.id) }, { contentTags: next });
  // v1.91.0 — a tag that doesn't exist yet: make it, then put it on this card.
  const addNewContentTag = async () => {
    const name = newTagName.trim();
    if (!task || !name) return;
    try {
      const { tag } = await api.post<{ tag: ContentTag }>('/tags/content', { name });
      await useLookupStore.getState().reloadContentTags();
      setContentTags([...(task.contentTags as ContentTag[]), tag]);
      setNewTagName(''); setNewTagOpen(false);
    } catch (e) {
      useToastStore.getState().show(e instanceof Error ? e.message : 'Could not add that tag.', 'error');
    }
  };
  const setStdTags = (next: StandardTag[]) =>
    task && void patchTask(task.id, { tagIds: next.map((t) => t.id) }, { tags: next });

  const availableAssignees = task ? lookup.assignees.filter((a) => !task.assignees.some((x) => x.id === a.id)) : [];
  const blockCandidates = task
    ? (areaTasks ?? []).filter(
        (t) => t.id !== task.id && !CLOSED.has(t.state) && !task.blockedBy.some((b) => b.id === t.id) && !task.blocks.some((b) => b.id === t.id),
      ).slice(0, 100)
    : [];
  const currentTags = isContent ? task?.contentTags ?? [] : task?.tags ?? [];
  const tagPool = (isContent ? lookup.contentTags : lookup.standardTags).filter((t) => !currentTags.some((x) => x.id === t.id));
  // A store this build doesn't know about (opened since, or added in Odoo Studio
  // before the cutover) still gets an option, labelled with its raw code so it
  // reads as SOMETHING. Without this a controlled <select> whose value isn't in
  // its options renders BLANK — which looks exactly like "this task has no store"
  // and is one stray click away from becoming true. Same "never drop a value you
  // can't resolve" rule the assignee and tag mappers follow.
  const locationOptions = task?.location && !STORE_LOCATIONS.some((s) => s.code === task.location)
    ? [...STORE_LOCATIONS, { code: task.location, name: task.location }]
    : STORE_LOCATIONS;

  return (
    <>
      {/* Full-window page, not a floating box (Allyson, 8/10: "A project in Odoo
          took up the whole window. It's what you are working on. Why is it so
          tiny?"). No backdrop, no margins, no rounded card — the task IS the
          screen while it's open. Close is ✕ / Esc / Back, same as before;
          click-outside-to-close is gone because there is no outside. */}
      {/* z-[58]: ABOVE the inbox overlay (55) so a card opened FROM the inbox
          closes back TO the inbox — "it ends up in the project section rather
          than the inbox" (team approvals doc, v1.85.0). Still under toasts. */}
      <div className="fixed inset-0 z-[58] flex flex-col overflow-hidden bg-canvas animate-fade-in">
        {/* Header */}
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-line px-4">
          <div className="flex min-w-0 items-center gap-2">
            {subNav ? (
              <>
                <button
                  // REPLACE, not push: task-to-task travel inside the open panel
                  // must keep the panel at ONE history entry, or ✕ (which consumes
                  // that entry) starts retracing every hop like a Back button.
                  // Same tx shape as prev/next below: open()'s sync prefix (veto +
                  // openId) runs inside the tx; the tx must never span an await.
                  onClick={() => nav.tx('replace', () => { void open(subNav.parentId); })}
                  title={`Back to parent: ${subNav.parentName}`}
                  className="flex min-w-0 items-center gap-1 rounded-md border border-line px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink"
                >
                  ↑ <span className="max-w-[180px] truncate">{subNav.parentName}</span>
                </button>
                {subNav.siblings.length > 1 && subNav.index >= 0 && (
                  <span className="flex shrink-0 items-center gap-0.5 text-xs text-faint">
                    <button
                      onClick={() => { const p = subNav.siblings[subNav.index - 1]; if (p) nav.tx('replace', () => { void open(p.id); }); }}
                      disabled={subNav.index <= 0}
                      title="Previous sub-task"
                      className="rounded px-1 py-1 text-muted hover:bg-hover hover:text-ink disabled:opacity-30"
                    >‹</button>
                    <span className="tabular-nums">{subNav.index + 1}/{subNav.siblings.length}</span>
                    <button
                      onClick={() => { const n = subNav.siblings[subNav.index + 1]; if (n) nav.tx('replace', () => { void open(n.id); }); }}
                      disabled={subNav.index >= subNav.siblings.length - 1}
                      title="Next sub-task"
                      className="rounded px-1 py-1 text-muted hover:bg-hover hover:text-ink disabled:opacity-30"
                    >›</button>
                  </span>
                )}
              </>
            ) : (
              <span className="min-w-0 truncate text-xs font-medium uppercase tracking-wide text-faint">
                {area?.name ?? 'Task'}
              </span>
            )}
          </div>
          {/* Two groups: secondary actions may scroll away on a narrow screen, but
              ✓ Complete / ⋯ / ✕ Close are PINNED. Previously the whole cluster was
              `shrink-0` inside an overflow-hidden parent, so on a phone the surplus —
              starting with ✕ Close — was clipped off-screen with no touch Esc, which
              could strand someone inside a task. */}
          <div className="flex min-w-0 items-center gap-1.5">
          <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto scrollbar-hide">
            {task && watchers && (
              <button
                onClick={() => void toggleWatch()}
                className={`flex shrink-0 items-center gap-1 rounded-md border px-2 py-1 text-xs transition ${watchers.watching ? 'border-brand/50 text-brand' : 'border-line text-muted hover:bg-hover hover:text-ink'}`}
                title={watchers.watching ? 'Watching — comments land in your inbox' : 'Watch — get every comment in your inbox'}
              >
                <Icon i={Eye} size={14} /> {watchers.watching ? 'Watching' : 'Watch'}
              </button>
            )}
            {task && (
              <button
                onClick={() => {
                  void navigator.clipboard.writeText(taskLink(task.id))
                    .then(() => toastWarn('Link copied — paste it anywhere.', 'info'))
                    .catch(() => toastWarn('Could not copy the link.', 'error'));
                }}
                className="flex shrink-0 items-center gap-1 rounded-md border border-line px-2 py-1 text-xs text-muted transition hover:bg-hover hover:text-ink"
                title="Copy a direct link to this task"
              >
                <Icon i={Link} size={14} /> Copy link
              </button>
            )}
            {task && !done && (
              isStuck ? (
                <button
                  onClick={() => void patchTask(task.id, { state: '01_in_progress' }, { state: '01_in_progress' })}
                  className="flex shrink-0 items-center gap-1 rounded-md border border-danger/50 px-2.5 py-1 text-xs font-semibold text-danger transition hover:bg-danger-soft"
                  title="Clear the stuck flag"
                >
                  <Icon i={RotateCcw} size={14} /> Unstuck
                </button>
              ) : (
                <button
                  onClick={() => setStuckOpen((v) => !v)}
                  className="flex shrink-0 items-center gap-1 rounded-md border border-line px-2.5 py-1 text-xs font-semibold text-muted transition hover:border-danger/60 hover:text-danger"
                  title="Flag as stuck / waiting on something"
                >
                  <Icon i={Ban} size={14} /> Stuck
                </button>
              )
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {task && (
              <button
                onClick={() => {
                  if (done) void patchTask(task.id, { state: '01_in_progress' }, { state: '01_in_progress' });
                  else void completeWithUndo(task.id, task.name, task.state);
                }}
                className={`flex items-center gap-1 rounded-md px-2.5 py-1 text-xs font-semibold transition ${done ? 'border border-line text-muted hover:bg-hover' : 'bg-brand text-page hover:brightness-110'}`}
              >
                {done ? <><Icon i={RotateCcw} size={14} /> Reopen</> : <><Icon i={Check} size={14} /> Mark complete</>}
              </button>
            )}
            <div className="relative">
              <button onClick={() => setMenuOpen((v) => !v)} className="rounded-md border border-line px-2 py-1 text-muted transition hover:bg-hover hover:text-ink" title="More actions"><Icon i={MoreHorizontal} size={14} /></button>
              {menuOpen && task && (
                <div className="absolute right-0 top-full z-20 mt-1 w-56 overflow-hidden rounded-md border border-line bg-canvas shadow-xl">
                  <button onClick={() => void duplicateTask()} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-hover"><Icon i={Copy} size={14} /> Duplicate task</button>
                  <button onClick={() => setMoveTarget((v) => !v)} className="flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] text-ink hover:bg-hover"><Icon i={FolderInput} size={14} /> Move to project…</button>
                  {moveTarget && (
                    <div className="max-h-48 overflow-y-auto border-t border-line">
                      {areasAll.filter((a) => a.id !== task.areaId).map((a) => (
                        <button key={a.id} onClick={() => void moveToProject(a.id)} className="flex w-full items-center px-4 py-1.5 text-left text-[12px] text-muted hover:bg-hover hover:text-ink">{a.name}</button>
                      ))}
                    </div>
                  )}
                  <button onClick={() => void archiveTask()} className="flex w-full items-center gap-2 border-t border-line px-3 py-2 text-left text-[13px] text-danger hover:bg-danger/10"><Icon i={Archive} size={14} /> Archive task</button>
                </div>
              )}
            </div>
            <button onClick={requestClose} aria-label="Close task" className="rounded p-1 text-muted hover:bg-hover hover:text-ink" title="Close (Esc)"><Icon i={X} size={14} /></button>
          </div>
          </div>
        </div>

        {/* Stuck reason capture / banner */}
        {task && stuckOpen && (
          <div className="shrink-0 border-b border-danger/30 bg-(--color-danger-tint) px-4 py-3">
            <div className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-danger"><Icon i={Ban} size={14} /> Mark stuck — what’s blocking it / what are we waiting on?</div>
            <textarea
              autoFocus
              value={stuckReason}
              onChange={(e) => setStuckReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void confirmStuck(); } else if (e.key === 'Escape') { setStuckOpen(false); setStuckReason(''); } }}
              rows={2}
              placeholder="e.g. Waiting on vendor quote · blocked by design approval · needs Pat sign-off"
              className="w-full resize-none rounded-md border border-line bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-danger"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => void confirmStuck()}
                disabled={stuckBusy || !stuckReason.trim()}
                className="rounded-md bg-danger-solid px-3 py-1 text-xs font-semibold text-white transition hover:brightness-110 disabled:opacity-40"
              >
                {stuckBusy ? 'Marking…' : 'Mark stuck'}
              </button>
              <button onClick={() => { setStuckOpen(false); setStuckReason(''); }} className="text-xs text-muted hover:text-ink">Cancel</button>
              <span className="ml-auto text-[10px] text-faint">Logged as a comment for the record</span>
            </div>
          </div>
        )}
        {task && changesOpen && (
          <div className="shrink-0 border-b border-warn/40 bg-warn-soft/40 px-4 py-3">
            <div className="mb-1.5 flex items-center gap-1 text-xs font-semibold text-warn"><Icon i={Pencil} size={14} /> Request changes — what needs to change?</div>
            <textarea
              autoFocus
              value={changesReason}
              onChange={(e) => setChangesReason(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void confirmChanges(); } else if (e.key === 'Escape') { setChangesOpen(false); setChangesReason(''); } }}
              rows={2}
              placeholder="e.g. Cut the last 2 seconds · colors look flat · swap the track"
              className="w-full resize-none rounded-md border border-line bg-canvas px-2.5 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-warn"
            />
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => void confirmChanges()}
                disabled={changesBusy || !changesReason.trim()}
                className="rounded-md bg-warn px-3 py-1 text-xs font-semibold text-page transition hover:brightness-110 disabled:opacity-40"
              >
                {changesBusy ? 'Sending…' : 'Request changes'}
              </button>
              <button onClick={() => { setChangesOpen(false); setChangesReason(''); }} className="text-xs text-muted hover:text-ink">Cancel</button>
              <span className="ml-auto text-[10px] text-faint">Posted to the conversation with your name on it</span>
            </div>
          </div>
        )}

        {/* Completed banner — the first thing you see on a closed task, so arriving
            from a notification you know it's done, and by whom, without hunting the
            right rail. Colours match the StateBadge (done green / cancelled red). */}
        {task && done && (
          (() => {
            const canceled = task.state === '1_canceled';
            const fg = canceled ? 'var(--chip-red-ink)' : 'var(--color-success)';
            const bg = canceled ? 'var(--color-danger-tint)' : 'var(--color-success-tint)';
            return (
              <div
                className="flex shrink-0 flex-wrap items-center gap-x-2 gap-y-1 border-b px-4 py-2.5 text-[13px]"
                style={{ background: bg, borderColor: `color-mix(in srgb, ${fg} 25%, transparent)`, color: fg }}
              >
                <span className="flex items-center gap-1 font-semibold">{canceled ? <><Icon i={Ban} size={14} /> Cancelled</> : <><Icon i={Check} size={14} /> Completed</>}</span>
                {(task.closedBy || task.closedByAt) && (
                  <span style={{ opacity: 0.85 }}>
                    {task.closedBy ? `· ${canceled ? 'cancelled' : 'closed'} by ${task.closedBy}` : '·'}
                    {task.closedByAt ? ` on ${fmtClosedAt(task.closedByAt)}` : ''}
                  </span>
                )}
                <button
                  onClick={() => void patchTask(task.id, { state: '01_in_progress' }, { state: '01_in_progress' })}
                  className="ml-auto flex items-center gap-1 rounded border px-2 py-0.5 text-xs font-semibold transition hover:brightness-125"
                  style={{ borderColor: `color-mix(in srgb, ${fg} 40%, transparent)` }}
                  title="Move it back to In Progress"
                >
                  <Icon i={RotateCcw} size={14} /> Reopen
                </button>
              </div>
            );
          })()
        )}

        {/* Body: document + conversation. §5A instant open: a seeded task
            renders the columns below WHILE loading (skeletons stand in for the
            parts a board row can't know), so the full-panel spinner survives
            only for cold deep links where there is nothing to paint from. */}
        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          {loading && !task && <div className="flex flex-1 items-center justify-center"><Spinner label="Loading task…" /></div>}
          {error && !task && <div className="flex flex-1 items-center justify-center text-sm text-danger">{error}</div>}

          {task && (
            <>
              {/* Work + conversation. Below xl: one page that scrolls top to
                  bottom (the old layout — right for laptops and phones). At
                  xl+: Odoo's split — the document on the left, the conversation
                  in its own column that is ON SCREEN the moment the task opens
                  (Allyson, 8/10: "Why do I have to scroll all the way down here
                  to see what is going on?"), each side scrolling independently. */}
              <div className="min-h-0 flex-1 overflow-y-auto xl:flex xl:overflow-hidden">
                {/* The document: what the task IS. No pb below xl — the
                    sub-tasks' bottom margin must collapse against the activity
                    section's top margin (24px, the pre-split rhythm); a padding
                    here would stack three gaps into 64px. */}
                <div className="px-6 pt-5 md:px-10 xl:min-h-0 xl:min-w-0 xl:flex-1 xl:overflow-y-auto xl:pb-5">
                  <textarea
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onBlur={saveName}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); (e.target as HTMLTextAreaElement).blur(); } }}
                    rows={1}
                    className="mb-4 w-full resize-none rounded-md bg-transparent text-2xl font-semibold leading-snug text-ink outline-none focus:bg-hover focus:px-2 focus:py-1 xl:text-display"
                  />

                  {/* One sentence answering "what does this card need?" — the
                      ten-second test made literal. Approve lives HERE, only
                      when the card is asking (it used to render on every card
                      for all 52 people: a standing mis-tap that silently
                      advanced real work). */}
                  {task.requestedBy && (
                    <div className="mb-3 flex items-center gap-2 text-[12.5px] text-muted">
                      <span aria-hidden>📨</span>
                      <span><strong className="text-ink">Request from {task.requestedBy.name}</strong> — whatever happens here, they'll be told automatically.</span>
                    </div>
                  )}
                  {strip && (
                    <div className={`mb-5 flex flex-wrap items-center gap-x-3 gap-y-2 rounded-lg border px-3 py-2 ${
                      strip.tone === 'danger' ? 'border-danger/40 bg-(--color-danger-tint) text-danger'
                      : strip.tone === 'warn' ? 'border-warn/40 bg-warn-soft/40 text-warn'
                      : 'border-line bg-canvas text-muted'}`}>
                      <span className="text-[13px]">{strip.text}</span>
                      {showApprove && (
                        <button
                          onClick={() => void patchTask(task.id, { state: '03_approved' }, { state: '03_approved' })}
                          className="ml-auto inline-flex items-center gap-1 rounded-md bg-success px-4 py-1.5 text-xs font-semibold text-page transition hover:brightness-110 active:scale-[0.98]"
                          title="Mark this work approved"
                        >
                          <Icon i={Check} size={14} /> Approve
                        </button>
                      )}
                    </div>
                  )}

                  {/* PHONE (below md): the conversation and its composer come
                      FIRST — "on a phone, the conversation and the one button
                      that matters sit right at the top" (the roadmap's phone
                      release). The desktop side-column slot empties itself on
                      phones (same component, one mount at a time). */}
                  {isPhone && !loading && task && (
                    <div className="mb-2">
                      <TaskComments key={`${task.id}-${commentsKey}-m`} taskId={task.id} activity={activityRows} />
                    </div>
                  )}

                  {/* The task's facts live IN the document, Odoo-style, right
                      under the title (Allyson, 8/10, pointing at Odoo's form:
                      "I feel like these should live somewhere on the screen
                      over here") — not in a far-edge rail that was mostly
                      empty air below "Blocked by". Two stacks, like Odoo's
                      form: identity on the left (stage, status, people, tags),
                      logistics on the right (location, dates, dependencies).
                      Renders from the seed, same as the old rail — fields are
                      board-row facts and must not wait for the fetch. */}
                  <div className="mb-6 grid gap-x-12 sm:grid-cols-2">
                    <div className="min-w-0">

                      <Prop label="Stage">
                        {/* An assignee who isn't a project member can open this task but can't
                            list the project's stages (that fetch 403s) — show them WHERE the
                            task is instead of an empty dropdown they can't read or use. */}
                        {stages?.length ? (
                          <select
                            value={task.stageId}
                            onChange={(e) => void moveStage(task.id, Number(e.target.value))}
                            className={`${fieldCtrl}`}
                          >
                            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            {!stages.some((s) => s.id === task.stageId) && <option value={task.stageId}>#{task.stageId}</option>}
                          </select>
                        ) : (
                          <span className="block truncate text-xs text-ink" title="Moving stages needs access to this project">
                            {task.stageName || `#${task.stageId}`}
                          </span>
                        )}
                      </Prop>

                      {/* "Status" not "State": it sat directly under "Stage" — two
                          near-homophones a non-technical reader can't tell apart — and its
                          values already read as statuses (In Progress / Approved / Done). */}
                      <Prop label="Status">
                        {/* The Approve button moved to the status strip under the
                            title (v1.70.0), GATED to cards genuinely asking —
                            this field keeps the dropdown and the chip. */}
                        {task.state === '03_approved' && (
                          <div className="mb-1.5">
                            <span className="inline-flex items-center gap-1 rounded-md bg-success-soft px-2 py-1 text-xs font-semibold text-success">
                              Approved <Icon i={Check} size={14} />
                            </span>
                          </div>
                        )}
                        <select
                          value={task.state}
                          onChange={(e) => {
                            const next = e.target.value as ApprovalState;
                            // Stuck always goes through the reason-capture flow — a Stuck
                            // banner must never point at a comment that doesn't exist.
                            if (next === STUCK && task.state !== STUCK) { setStuckOpen(true); return; }
                            // Same capture-first rule as Stuck: the "no" must carry its why.
                            if (next === '02_changes_requested' && task.state !== next) { setChangesOpen(true); return; }
                            // Closing from the dropdown gets the same Undo as ✓ Mark complete.
                            if ((next === '1_done' || next === '1_canceled') && next !== task.state) {
                              void closeWithUndo(task.id, task.name, task.state, next, undefined, task.stateReason ?? null);
                              return;
                            }
                            void patchTask(task.id, { state: next }, { state: next });
                          }}
                          className={`${fieldCtrl}`}
                        >
                          {STATES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                        </select>
                      </Prop>

                      <Prop label="Priority">
                        <button
                          onClick={() => void patchTask(task.id, { priority: task.priority === '1' ? '0' : '1' }, { priority: task.priority === '1' ? '0' : '1' })}
                          className={`flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${task.priority === '1' ? 'border-warn text-warn' : 'border-line text-muted hover:bg-hover'}`}
                        >
                          {task.priority === '1' ? <><Icon i={Star} size={14} className="fill-current" /> High</> : <><Icon i={Star} size={14} /> Normal</>}
                        </button>
                      </Prop>

                      <Prop label="Assignees">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {task.assignees.map((a) => (
                            <span key={a.id} className="inline-flex items-center gap-1 rounded-full bg-hover py-0.5 pl-1 pr-1.5 text-xs">
                              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full text-[8px] font-medium text-white" style={{ backgroundColor: avatarColor(a.name) }}>{initials(a.name)}</span>
                              <span className="text-ink">{a.name}</span>
                              <button onClick={() => setAssignees(task.assignees.filter((x) => x.id !== a.id))} className="text-faint hover:text-danger" title="Remove"><Icon i={X} size={14} /></button>
                            </span>
                          ))}
                          {availableAssignees.length > 0 && (
                            <select
                              value=""
                              onChange={(e) => {
                                const a = lookup.assignees.find((x) => x.id === Number(e.target.value));
                                if (!a) return;
                                setAssignees([...task.assignees, a]);
                                if (!isMember(a.id)) {
                                  // Assignment now grants access to THIS task (and only this
                                  // task) — say what actually happens instead of the old
                                  // "they can't see it", which was true and unhelpful.
                                  toastWarn(`${a.name} isn't on this project, so they'll see just this task — not the rest of the board. Add them in Settings if they need the whole project.`, 'info');
                                }
                              }}
                              className={`${fieldCtrl} py-0.5 text-xs`}
                            >
                              <option value="">+ Add person</option>
                              {availableAssignees.map((a) => (
                                <option key={a.id} value={a.id}>{isMember(a.id) ? a.name : `${a.name} — this task only`}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </Prop>

                      <Prop label={isContent ? 'Content tags' : 'Tags'}>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {currentTags.map((t) => (
                            <span key={t.id} className="inline-flex items-center gap-1 rounded bg-hover px-1.5 py-0.5 text-[11px] text-ink">
                              {t.name}
                              <button
                                onClick={() => (isContent ? setContentTags((task.contentTags as ContentTag[]).filter((x) => x.id !== t.id)) : setStdTags((task.tags as StandardTag[]).filter((x) => x.id !== t.id)))}
                                className="text-faint hover:text-danger"
                                title="Remove"
                              ><Icon i={X} size={14} /></button>
                            </span>
                          ))}
                          {(tagPool.length > 0 || isContent) && !newTagOpen && (
                            <select
                              value=""
                              onChange={(e) => {
                                const v = e.target.value;
                                if (v === '__new') { setNewTagOpen(true); return; }
                                if (v === '__manage') { setManageTagsOpen(true); return; }
                                const id = Number(v);
                                if (isContent) { const t = lookup.contentTags.find((x) => x.id === id); if (t) setContentTags([...task.contentTags, t]); }
                                else { const t = lookup.standardTags.find((x) => x.id === id); if (t) setStdTags([...task.tags, t]); }
                              }}
                              className={`${fieldCtrl} py-0.5 text-xs`}
                            >
                              <option value="">+ Add tag</option>
                              {tagPool.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                              {isContent && <option value="__new">＋ New tag…</option>}
                              {isContent && <option value="__manage">Manage tags…</option>}
                            </select>
                          )}
                          {isContent && newTagOpen && (
                            <span className="inline-flex items-center gap-1">
                              <input
                                autoFocus
                                value={newTagName}
                                onChange={(e) => setNewTagName(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') void addNewContentTag(); if (e.key === 'Escape') { setNewTagOpen(false); setNewTagName(''); } }}
                                placeholder="New tag name"
                                maxLength={60}
                                className={`${fieldCtrl} w-36 py-0.5 text-xs`}
                              />
                              <button onClick={() => void addNewContentTag()} disabled={!newTagName.trim()} className="rounded border border-line px-1.5 py-0.5 text-[11px] text-ink hover:bg-hover disabled:opacity-40">Add</button>
                              <button onClick={() => { setNewTagOpen(false); setNewTagName(''); }} className="text-faint hover:text-ink" title="Cancel"><Icon i={X} size={14} /></button>
                            </span>
                          )}
                        </div>
                      </Prop>

                      {manageTagsOpen && <ContentTagsModal onClose={() => { setManageTagsOpen(false); void useLookupStore.getState().reloadContentTags(); }} />}

                      {/* Which store the work is for. Sits right after the tags because
                          that is where Odoo's task form puts it (Studio view 5523), and
                          the teams who actually use it — Events, Accounting, Maintenance,
                          N.C. Events — have four years of muscle memory for that spot.
                          Shown on every task, not gated by project: it is a per-team
                          habit rather than a company-wide field, and a team that wants
                          to start using it shouldn't need a config change to do so. */}

                      <Prop label="Location">
                        <select
                          value={task.location ?? ''}
                          onChange={(e) => {
                            // '' is the CLEAR, not a seventh store — a task filed against
                            // the wrong store has to be correctable back to none.
                            const next = e.target.value || null;
                            void patchTask(task.id, { location: next }, { location: next });
                          }}
                          className={`${fieldCtrl}`}
                        >
                          <option value="">No location</option>
                          {locationOptions.map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}
                        </select>
                      </Prop>

                    </div>
                    <div className="min-w-0">

                      {/* The key date must edit the SAME field the area is keyed on, or the
                          label lies: Events teams key on date_deadline, so an "Event date"
                          box bound to filmDate would write a field nothing reads and the
                          card would never move on the calendar. When the key date IS the
                          deadline, render one labelled field, not two for one value. */}
                      {/* A board can declare both dates PAST (Area.hideKeyDates — Content
                          Calendar: the video is already shot and posted). Then neither box
                          is drawn: the stored values are untouched and come back with the
                          card if it ever returns to a production board. Hidden, never
                          blanked — an empty box invites someone to type into a field this
                          board has just said it doesn't use. */}
                      {!area?.hideKeyDates && (
                        <>
                          {area?.keyDateLabel && area.calendarDateField === 'film_date' && (
                            <Prop label={area.keyDateLabel}>
                              <input type="date" value={task.filmDate ?? ''} onChange={(e) => void patchTask(task.id, { filmDate: e.target.value || null }, { filmDate: e.target.value || null })} className={`${fieldCtrl}`} />
                            </Prop>
                          )}
                          <Prop label={area?.keyDateLabel && area.calendarDateField === 'date_deadline' ? area.keyDateLabel : 'Deadline'}>
                            <input type="date" value={task.deadline ?? ''} onChange={(e) => void patchTask(task.id, { deadline: e.target.value || null }, { deadline: e.target.value || null })} className={`${fieldCtrl}`} />
                          </Prop>
                        </>
                      )}

                      {/* Recurring events (Azriella): ONE card that rolls itself forward
                          once the date passes, re-opening the checklist — rather than a
                          separate card per occurrence. */}
                      <Prop label="Repeats">
                        <select
                          value={task.repeat ? `${task.repeat.every}:${task.repeat.interval}` : ''}
                          onChange={(e) => {
                            const v = e.target.value;
                            const body = v ? { every: v.split(':')[0], interval: Number(v.split(':')[1]) } : { every: null };
                            void api.put<{ task: TaskDetail }>(`/tasks/${task.id}/repeat`, body)
                              .then((r: { task: TaskDetail }) => useTaskDetailStore.setState({ task: r.task }))
                              // Controlled select: on failure the store is untouched, so the
                              // dropdown snaps back rather than showing a repeat that isn't set.
                              .catch((err: unknown) => toastWarn(err instanceof Error ? err.message : 'Could not change the repeat.', 'error'));
                          }}
                          className={`${fieldCtrl}`}
                        >
                          <option value="">Doesn't repeat</option>
                          <option value="week:1">Every week</option>
                          <option value="week:2">Every 2 weeks</option>
                          <option value="month:1">Every month</option>
                        </select>
                        {task.repeat && (
                          <p className="mt-1 text-[10px] leading-snug text-faint">
                            After the date passes, the sub-tasks re-open and the date moves on.
                            Each round is logged in the comments so you can look back.
                          </p>
                        )}
                      </Prop>

                      {isContent && (
                        <Prop label="Views">
                          <div className="flex flex-wrap items-center gap-2">
                            <input
                              key={`views-${task.id}-${task.viewCount ?? 'x'}`}
                              defaultValue={fmtViews(task.viewCount)}
                              onBlur={(e) => {
                                const v = parseViews(e.target.value);
                                if (Number.isNaN(v)) { toastWarn('Views should be a number — "40k" and "2.4m" work too.', 'error'); return; }
                                if (v !== (task.viewCount ?? null)) void patchTask(task.id, { viewCount: v }, {});
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                              placeholder="type the count…"
                              className={`${fieldCtrl} w-28`}
                              title="Instagram views — typing a count files a published card into its performance shelf"
                            />
                            <input
                              key={`ref-${task.id}-${task.refViewCount ?? 'x'}`}
                              defaultValue={task.refViewCount != null ? fmtViews(task.refViewCount) : ''}
                              onBlur={(e) => {
                                const v = parseViews(e.target.value);
                                if (Number.isNaN(v)) { toastWarn('Views should be a number — "40k" and "2.4m" work too.', 'error'); return; }
                                if (v !== (task.refViewCount ?? null)) void patchTask(task.id, { refViewCount: v }, {});
                              }}
                              onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
                              placeholder="theirs…"
                              className={`${fieldCtrl} w-24 text-faint`}
                              title="The reference reel's views — so the card can say how ours compares"
                            />
                            {task.viewCount != null && task.refViewCount != null && task.refViewCount > 0 && (
                              <span className="text-[11px] text-faint">ours is {Math.round((task.viewCount / task.refViewCount) * 100)}% of theirs</span>
                            )}
                          </div>
                        </Prop>
                      )}
                      <Prop label="Blocked by">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {task.blockedBy.map((r) => (
                            <span key={r.id} className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[11px] ${r.state === '1_done' || r.state === '1_canceled' ? 'border-line text-faint line-through' : 'border-danger/40 text-danger'}`}>
                              {/* REPLACE (in-panel travel) — may cross into another project;
                                  dismissTask's close matrix then lands ✕ on THAT project. */}
                              <Icon i={Ban} size={14} /> <button onClick={() => nav.tx('replace', () => { void open(r.id); })} className="hover:underline">{r.name}</button>
                              <button
                                onClick={() => void editBlockedBy((ids) => ids.filter((x) => x !== r.id))}
                                className="text-faint hover:text-danger" title="Remove dependency"
                              ><Icon i={X} size={14} /></button>
                            </span>
                          ))}
                          {blockCandidates.length > 0 && (
                            <select
                              value=""
                              onChange={(e) => {
                                const id = Number(e.target.value);
                                if (id) void editBlockedBy((ids) => (ids.includes(id) ? ids : [...ids, id]));
                              }}
                              className={`${fieldCtrl} py-0.5 text-xs`}
                            >
                              <option value="">+ Add blocker</option>
                              {blockCandidates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                            </select>
                          )}
                          {task.blockedBy.length === 0 && blockCandidates.length === 0 && <span className="text-faint">—</span>}
                        </div>
                      </Prop>

                      {watchers && watchers.watchers.length > 0 && (
                        <Prop label="Watchers">
                          <AssigneeStack assignees={watchers.watchers} max={6} />
                        </Prop>
                      )}

                      {task.blocks.length > 0 && (
                        <Prop label="Blocks">
                          <div className="flex flex-wrap items-center gap-1.5">
                            {task.blocks.map((r) => (
                              // REPLACE (in-panel travel) — same rule as the Blocked-by refs.
                              <button key={r.id} onClick={() => nav.tx('replace', () => { void open(r.id); })} className="inline-flex items-center gap-1 rounded border border-line px-1.5 py-0.5 text-[11px] text-muted hover:bg-hover hover:text-ink">
                                {r.name}
                              </button>
                            ))}
                          </div>
                        </Prop>
                      )}

                    </div>
                  </div>

                  {loading ? (
                    /* §5A instant open: the seed painted the title + fact grid; what a
                       board row can't know — description, attachments, sub-tasks,
                       comments — holds its SHAPE until the fetch lands. Ghosts,
                       not a spinner: the layout is already known. Keeping this
                       zone unmounted while loading is also the first line of the
                       seed guard — a seed's empty descriptionHtml must never be
                       clickable into an editor (see the baseline effect). */
                    <>
                      <Skeleton className="mb-6 h-24" />
                      <Skeleton className="h-40" />
                    </>
                  ) : (
                  <>
                  {editingDesc ? (
                    <DescriptionEditor
                      taskId={task.id}
                      initialHtml={task.descriptionHtml}
                      onSave={async (html, baseOverride, auto) => {
                        // baseDescriptionHtml is the description FROM WHEN EDITING
                        // BEGAN — a real concurrent description change 409s (and this
                        // editor + text stays open); unrelated field edits don't.
                        // A restored draft supplies the baseline IT was written
                        // against, so a stale draft can't overwrite newer text.
                        markSelfDescWrite(html);
                        const ok = await patchTask(task.id, { descriptionHtml: html, baseDescriptionHtml: baseOverride ?? descBaseHtml.current });
                        endSelfDescWrite();
                        if (ok) {
                          // The stored html is the SERVER's version (it rewrites image
                          // URLs), and that is what the next save must be compared to.
                          const stored = useTaskDetailStore.getState().task?.descriptionHtml ?? html;
                          markSelfDescWrite(stored, false);
                          descBaseHtml.current = stored;
                          if (!auto) setEditingDesc(false); // autosave keeps you typing
                          return stored;
                        }
                        // Failure keeps the editor + text open — AND refreshes the
                        // task underneath, then re-baselines so a deliberate retry
                        // (overwrite) actually saves (no 409 dead-end loop).
                        await useTaskDetailStore.getState().refresh();
                        descBaseHtml.current = useTaskDetailStore.getState().task?.descriptionHtml ?? descBaseHtml.current;
                        return null;
                      }}
                      onUnsavedChange={setDescUnsaved}
                      onCancel={() => { setDescUnsaved(false); setEditingDesc(false); }}
                    />
                  ) : (
                    <div
                      onClick={() => setEditingDesc(true)}
                      className="group/desc mb-6 -mx-2 cursor-text rounded-lg px-2 py-1 transition hover:bg-hover/50"
                      title="Click to edit description"
                    >
                      {task.descriptionHtml ? (
                        <>
                        <div
                          /* The brief is what people READ ("the stuff I am
                             going to actually read gets this small space") —
                             it gets reading type, a step up from the 13px
                             facts and chrome around it. */
                          className="cg-prose cg-prose-reading"
                          onClick={(e) => {
                            // A checkbox toggles + saves in place (no edit mode).
                            const target = e.target as HTMLElement;
                            const html = toggleCheckboxAt(target);
                            if (html !== null) {
                              e.stopPropagation();
                              // Baseline = the CURRENT store value; update it optimistically
                              // so a rapid 2nd tick (before this PATCH returns) chains off the
                              // new content instead of a stale base (which self-409'd).
                              const base = useTaskDetailStore.getState().task?.descriptionHtml ?? task.descriptionHtml;
                              const cur = useTaskDetailStore.getState().task;
                              if (cur && cur.id === task.id) useTaskDetailStore.setState({ task: { ...cur, descriptionHtml: html } });
                              void patchTask(task.id, { descriptionHtml: html, baseDescriptionHtml: base }).then((ok) => {
                                if (!ok) {
                                  toggleCheckboxAt(target); // revert the DOM
                                  const c = useTaskDetailStore.getState().task;
                                  if (c && c.id === task.id) useTaskDetailStore.setState({ task: { ...c, descriptionHtml: base } });
                                }
                              });
                            }
                          }}
                          dangerouslySetInnerHTML={{ __html: task.descriptionHtml }}
                        />
                        {/* Pasted links become preview cards (Spencer's ask), mounted
                            under the rendered description. The wrapper stops click
                            bubbling so opening a card — or clicking the gap between
                            cards — follows the link instead of tripping the
                            click-to-edit handler on the container above. */}
                        <div onClick={(e) => e.stopPropagation()}>
                          <LinkPreviewCards html={task.descriptionHtml} />
                        </div>
                        </>
                      ) : (
                        <p className="text-sm text-faint">No description — click to write one.</p>
                      )}
                      <div className="mt-1 flex items-center gap-1 text-[10px] text-faint opacity-0 transition group-hover/desc:opacity-100"><Icon i={Pencil} size={14} /> click to edit</div>
                    </div>
                  )}

                  {/* Content teams live and die by the cut; other areas stay
                      quiet unless a cut already exists. */}
                  {(isContent || (task.cuts?.length ?? 0) > 0) && (
                    <CutsSection taskId={task.id} cuts={task.cuts ?? []} />
                  )}

                  <AttachmentsSection taskId={task.id} />

                  <div className="mb-6">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-faint">
                      Sub-tasks{task.children.length ? ` · ${task.children.length}` : ''}
                    </div>
                    <div className="overflow-hidden rounded-lg border border-line">
                      <SubtaskList
                        parentId={task.id}
                        subtasks={task.children}
                        // REPLACE (in-panel travel): the panel swaps tasks in place and
                        // keeps its single history entry — the parent link (↑) is the
                        // way back up, not the browser's Back stack.
                        onOpen={(id) => nav.tx('replace', () => { void open(id); })}
                      />
                      <div className="flex items-center gap-2 px-3 py-1.5">
                        <span className="text-faint"><Icon i={Plus} size={14} /></span>
                        <input
                          value={subDraft}
                          onChange={(e) => setSubDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && subDraft.trim() && !subBusy) {
                              e.preventDefault();
                              setSubBusy(true);
                              void createTask({ areaId: task.areaId, name: subDraft.trim(), parentId: task.id, stageId: task.stageId })
                                // refresh() (not open) so mid-description-edit adding a
                                // sub-task doesn't trip the unsaved-edits discard prompt.
                                .then((created) => { if (created) { setSubDraft(''); void useTaskDetailStore.getState().refresh(); } })
                                .finally(() => setSubBusy(false));
                            }
                          }}
                          placeholder={subBusy ? 'Adding…' : 'Add a sub-task…  (Enter)'}
                          disabled={subBusy}
                          className="w-full bg-transparent py-0.5 text-[13px] text-ink outline-none placeholder:text-faint"
                        />
                      </div>
                    </div>
                  </div>

                  </>
                  )}
                </div>

                {/* The conversation: what is HAPPENING. At xl it gets its own
                    column — composer stays pinned at the top, history scrolls
                    beneath it (TaskComments switches to flex layout via xl:
                    classes). Below xl it renders exactly where it always did:
                    after the document, in the one page-level scroll. */}
                <div className="px-6 pb-5 md:px-10 xl:flex xl:min-h-0 xl:w-[46%] xl:max-w-[900px] xl:shrink-0 xl:flex-col xl:border-l xl:border-line xl:px-8 xl:py-5">
                  {loading ? (
                    /* xl-only: below xl the pre-split loading state was exactly
                       two doc skeletons — keep it that way. */
                    <Skeleton className="hidden h-40 xl:block" />
                  ) : isPhone ? null : (
                    <TaskComments key={`${task.id}-${commentsKey}`} taskId={task.id} activity={activityRows} />
                  )}
                </div>
              </div>


            </>
          )}
        </div>
      </div>
    </>
  );
}
